/**

 * Arena / Tournament access (Arena Owner / Tournament Manager / Spectator).

 *

 * These are **access levels on an Arena**, scoped by User Account (`uid`).

 * They are NOT global account roles.

 *

 * **Player is not a permission role.** A Player is a tournament participant

 * (`players/{playerId}` / Entry). An authenticated User may also be a Player,

 * but viewing/editing rights come from Owner / Manager / Spectator — never from

 * “being a Player.”

 *

 * **Spectator** is an access level (anonymous or signed-in read-only viewer),

 * not a domain aggregate.

 */



import { isCompetitionDraft, isCompetitionReadOnly, isCompetitionSoftDeleted } from "../domain/competition/competitionStatusTransitions";



/**

 * Arena Owner — createdBy / organizerUid.

 * @param {object | null | undefined} competition

 * @param {string | null | undefined} uid  User Account id

 */

export function isCompetitionOwner(competition, uid) {

  if (!competition || !uid) return false;

  return competition.createdBy === uid || competition.organizerUid === uid;

}



/**

 * Tournament Manager — listed in managerUids, or Owner (Owners have manager capabilities).

 * @param {object | null | undefined} competition

 * @param {string | null | undefined} uid  User Account id

 */

export function isCompetitionManager(competition, uid) {

  if (!competition || !uid) return false;

  if (isCompetitionOwner(competition, uid)) return true;

  const managers = competition.managerUids || [];

  return Array.isArray(managers) && managers.includes(uid);

}

/**
 * Invited match official. Not a player entry and not a tournament manager.
 * @param {object | null | undefined} competition
 * @param {string | null | undefined} uid
 */
export function isTournamentReferee(competition, uid) {
  if (!competition || !uid) return false;
  const refs = competition.refereeUids || [];
  return Array.isArray(refs) && refs.includes(uid);
}

/**
 * Play, score, and call matches. Settings and the draw stay with the organizer.
 * @param {object | null | undefined} competition
 * @param {string | null | undefined} uid
 */
export function canManageTournamentMatches(competition, uid) {
  return isCompetitionManager(competition, uid) || isTournamentReferee(competition, uid);
}



/**

 * Spectator access — read-only viewer (anonymous or signed-in without owner/manager).

 * Not a domain entity.

 * @param {object | null | undefined} competition

 * @param {string | null | undefined} uid  User Account id; null/undefined = anonymous spectator

 */

export function isCompetitionSpectator(competition, uid) {

  if (!uid) return true;

  return !isCompetitionManager(competition, uid);

}



/**

 * @param {object | null | undefined} competition

 * @param {string | null | undefined} uid

 */

export function canEditCompetition(competition, uid) {

  if (!competition || !uid) return false;

  if (isCompetitionReadOnly(competition.status)) return false;

  return isCompetitionManager(competition, uid);

}



/**

 * @param {object | null | undefined} competition

 * @param {string | null | undefined} uid

 */

export function canPublishCompetition(competition, uid) {

  if (!competition || !uid) return false;

  if (!isCompetitionDraft(competition.status)) return false;

  return isCompetitionOwner(competition, uid);

}



/**

 * @param {object | null | undefined} competition

 * @param {string | null | undefined} uid

 */

export function canArchiveCompetition(competition, uid) {

  if (!competition || !uid) return false;

  if (!isCompetitionOwner(competition, uid)) return false;

  const status = competition.status;

  return status === "completed";

}



/**

 * @param {object | null | undefined} competition

 * @param {string | null | undefined} uid

 */

export function canDeleteDraftCompetition(competition, uid) {

  if (!competition || !uid) return false;

  if (!isCompetitionOwner(competition, uid)) return false;

  return isCompetitionDraft(competition.status);

}



/**

 * Soft-delete published/live competitions (keeps match history & stats).

 * Drafts cannot soft-delete — use hard delete.

 * @param {object | null | undefined} competition

 * @param {string | null | undefined} uid

 */

export function canSoftDeleteCompetition(competition, uid) {

  if (!competition || !uid) return false;

  if (!isCompetitionOwner(competition, uid)) return false;

  const status = competition.status;

  if (isCompetitionDraft(status)) return false;

  if (isCompetitionSoftDeleted(status)) return false;

  return true;

}



/**

 * Permanent delete of the competition document (owner).

 * Match history rows are not cascade-deleted.

 * @param {object | null | undefined} competition

 * @param {string | null | undefined} uid

 */

export function canHardDeleteCompetition(competition, uid) {

  if (!competition || !uid) return false;

  return isCompetitionOwner(competition, uid);

}



/**

 * Show Delete control: owner of a draft, soft-deleting, or already soft-deleted (to purge).

 * @param {object | null | undefined} competition

 * @param {string | null | undefined} uid

 */

export function canDeleteCompetition(competition, uid) {

  return (

    canDeleteDraftCompetition(competition, uid) ||

    canSoftDeleteCompetition(competition, uid) ||

    (canHardDeleteCompetition(competition, uid) && isCompetitionSoftDeleted(competition?.status))

  );

}



/**

 * @param {object | null | undefined} competition

 * @param {string | null | undefined} uid

 */

export function canUpdateCompetitionSettings(competition, uid) {

  return canEditCompetition(competition, uid);

}



/**

 * Resolve Competition access level for a User Account.

 * @param {object | null | undefined} competition

 * @param {string | null | undefined} uid

 * @returns {'owner'|'manager'|'spectator'}

 */

export function resolveCompetitionRole(competition, uid) {

  if (isCompetitionOwner(competition, uid)) return "owner";

  if (isCompetitionManager(competition, uid)) return "manager";

  return "spectator";

}



/**

 * User-facing Arena / Tournament access label.

 * @param {'owner'|'manager'|'spectator'|string | null | undefined} role

 */

export function competitionAccessLabel(role) {
  if (role === "owner") return "Arena Owner";
  if (role === "manager") return "Tournament Manager";
  return "Spectator";
}


